import React from "react";
import * as Router from "next/router";
import styled from "styled-components";

import * as u from "../../utils";
import * as gtag from "../../utils/gtag";
import CONTENT_TAGS from "../../utils/content-tags";
import firebase from "../../firebase.client";
import * as UserContext from "../../components/UserContext";
import * as T from "../../components/Typography";
import PromoBanner from "../../components/PromoBanner";
import LessonCard from "../../components/AcademyLessonCard";
import Button from "../../components/Button";
import RxSubNavAcademy from "../../components/ui/navigation/RxSubNavAcademy";
import UpsellModal from "../../components/UpsellModal";
import DropdownFilter from "../../components/DropdownFilter";
import { VerticalDivider } from "../../components/Utils";
import Search from "../../components/Search";
import TickInput from "../../components/TickInput";
import { Device } from "@/utils/device";

export default function Academy(props) {
  const userInfo = React.useContext(UserContext.Context);
  const { hasLoaded } = userInfo;
  const router = Router.useRouter();
  const [searchValue, setSearchValue] = React.useState();
  const [savedCourseIds, setSavedCourseIds] = React.useState([]);
  const [modalType, setModalType] = React.useState(false);
  const [filteredCourses, setFilteredCourses] = React.useState(props.lessons);
  const [showAccessCourses, setShowAccessCourses] = React.useState(false);
  const [showMoreCourses, setShowMoreCourses] = React.useState(false);
  const isLoggedIn = !!userInfo.id;
  const canFilterAccess = isLoggedIn && !userInfo.isPaid;

  const [selectedTags, setSelectedTags] = React.useState([]);

  React.useEffect(filterCourses, [selectedTags, showAccessCourses]);
  React.useEffect(() => {
    getMyCourses();
  }, [userInfo]);
  React.useEffect(searchCourses, [searchValue]);
  React.useEffect(updateUrl, [searchValue, selectedTags]);
  React.useEffect(() => {
    const tags = JSON.parse(router.query.tags || "[]");
    setSelectedTags(tags);
  }, [router.query.tags]);

  const shownCourses = showMoreCourses
    ? filteredCourses
    : filteredCourses.slice(0, 12);
  const hideShowMoreButton = showMoreCourses || filteredCourses.length < 9;

  return (
    hasLoaded && (
      <>
        <RxSubNavAcademy />
        <S.Container>
          <UpsellModal type={modalType} setType={setModalType} />

          <S.SearchAndFiltersContainer>
            <S.Left></S.Left>
            <VerticalDivider />
            <DropdownFilter
              name="academy-topic"
              hint="Filter courses by topic"
              list={CONTENT_TAGS}
              value={selectedTags}
              onSelect={setSelectedTags}
            />
            <VerticalDivider />
            <Search
              initialValue={router.query.search}
              onSearch={(searchValue) => setSearchValue(searchValue)}
              name="academy"
            />
          </S.SearchAndFiltersContainer>

          <PromoBanner />
          {canFilterAccess && (
            <S.Filters>
              <TickInput
                name="filter"
                isTrue={showAccessCourses}
                onChange={() => setShowAccessCourses(!showAccessCourses)}
              >
                <T.CM>Only show courses available with my membership</T.CM>
              </TickInput>
            </S.Filters>
          )}
          <S.Lessons data-cy="courses">{shownCourses.map(getLesson)}</S.Lessons>
          {!filteredCourses.length && (
            <T.CM>No courses found. Remove some filters and try again</T.CM>
          )}
          <S.ShowMoreButton
            data-cy={"academy-show-more"}
            onClick={goShowMoreCourses}
            hide={hideShowMoreButton}
          />
        </S.Container>
      </>
    )
  );

  function updateUrl() {
    if (searchValue === undefined && selectedTags.length === 0) return;

    let queryStringParams = new URLSearchParams(router.query);

    if (searchValue?.length) {
      queryStringParams.set("search", searchValue);
    } else {
      queryStringParams.delete("search");
    }
    if (selectedTags?.length) {
      queryStringParams.set("tags", JSON.stringify(selectedTags));
    } else {
      queryStringParams.delete("tags");
    }

    return router.replace(`/academy?${queryStringParams.toString()}`);
  }

  function filterCourses() {
    setFilteredCourses(getFilteredLessons());

    function getFilteredLessons() {
      let filteredLessons = [...props.lessons];

      if (showAccessCourses && !userInfo.isPaid) {
        filteredLessons = filteredLessons.filter((lesson) => lesson.isFree);
      }

      if (selectedTags.length === 0) return filteredLessons;
      return filteredLessons.filter(filterLessonsByContentTag);
    }

    function filterLessonsByContentTag(lesson) {
      const lessonTags = lesson.contentfulMetadata.tags.map(
        (tag) => tag.name.split(": ")[1]
      );
      return lessonTags?.some((tag) => selectedTags.includes(tag));
    }
  }

  function searchCourses() {
    if (!searchValue) filterCourses();
    else {
      const matchingLessons = props.lessons.filter(
        (lesson) =>
          lesson.lessonName
            ?.toLowerCase()
            .includes(searchValue.toLowerCase()) ||
          lesson.lessonIntroductionText
            ?.toLowerCase()
            .includes(searchValue.toLowerCase())
      );
      setFilteredCourses(matchingLessons);
    }
  }

  async function getMyCourses() {
    if (!userInfo.id) return;
    const data = (
      await firebase.firestore().collection("users").doc(userInfo.id).get()
    ).data();
    if (data?.savedCourses) setSavedCourseIds(data.savedCourses);
    if (data?.courses?.length) addCourseProgress(data.courses);

    function addCourseProgress(myCourses) {
      props.lessons.forEach((lesson) => {
        const myStartedCourse = myCourses.find(
          (course) => course.id === lesson.sys.id
        );
        if (!myStartedCourse) return;
        const lessonLength = lesson.lessonActivitiesCollection.total;
        const completedLessonLength = myStartedCourse.completedLessons.length;
        const progress = completedLessonLength / lessonLength;
        lesson.progress = progress;
      });
    }
  }

  function getLesson(lesson) {
    return (
      <LessonCard
        key={lesson.sys.id}
        savedCourseIds={savedCourseIds}
        setSavedCourseIds={setSavedCourseIds}
        setModalType={setModalType}
        {...lesson}
      />
    );
  }

  function goShowMoreCourses() {
    gtag.event({
      name: gtag.GA_EVENT.ACADEMY_SHOW_MORE,
      category: "Academy",
      label: "Show more courses",
      value: "courses",
    });

    return setShowMoreCourses(true);
  }
}

export async function getStaticProps({ locale }) {
  const axios = require("axios");
  const { CONTENTFUL_GENERAL } = require("../../config");
  const lessonData1 = (await axios.post(CONTENTFUL_GENERAL, getLessonsQuery(0)))
    .data.data.lessonCollection.items;
  const lessonData2 = (
    await axios.post(CONTENTFUL_GENERAL, getLessonsQuery(100))
  ).data.data.lessonCollection.items;
  const lessonData3 = (
    await axios.post(CONTENTFUL_GENERAL, getLessonsQuery(200))
  ).data.data.lessonCollection.items;
  const lessonData4 = (
    await axios.post(CONTENTFUL_GENERAL, getLessonsQuery(300))
  ).data.data.lessonCollection.items;
  const lessonData5 = (
    await axios.post(CONTENTFUL_GENERAL, getLessonsQuery(400))
  ).data.data.lessonCollection.items;
  const lessonData = [
    ...lessonData1,
    ...lessonData2,
    ...lessonData3,
    ...lessonData4,
    ...lessonData5,
  ];
  const lessons = lessonData.filter(byLocale);
  const courseTags = (await axios.post(CONTENTFUL_GENERAL, getTagsQuery())).data
    .data.tagCollection.items;
  const categoryTags = (
    await axios.post(CONTENTFUL_GENERAL, getCategoryQuery())
  ).data.data.categoryTagCollection.items;

  return { props: { lessons, courseTags, categoryTags }, revalidate: 3600 };

  function byLocale(item) {
    if (!item.locale) return true;
    return item.locale.includes(locale);
  }

  function getLessonsQuery(skip) {
    return {
      query: `{
        lessonCollection (limit: 100, skip: ${skip}) {
          items {
            locale
            lessonName
            lessonIntroductionText
            courseTags
            categoryTags
            urlSlug
            lessonIntroductionText
            isFree
            featuredCourseDescription
            featuredCourseMobileImage { url title }
            featuredCourseImage { url title }
            coverImage { title url }
            sys { id publishedAt }
            lessonActivitiesCollection { total }
            contentfulMetadata {
              tags {
                id
                name
              }
            }
          }
        }
      }`,
    };
  }

  function getCategoryQuery() {
    return {
      query: `{
        categoryTagCollection {
          items { name }
        }
      }`,
    };
  }

  function getTagsQuery() {
    return {
      query: `{
        tagCollection {
          items { name }
        }
      }`,
    };
  }
}

const S = {};

S.Container = styled.div`
  max-width: 1440px;
  margin: auto;
  padding: 20px 16px;

  @media ${u.device.tablet} {
    padding: 20px;
  }

  @media ${u.device.desktop} {
    padding: 40px;
  }
`;

S.H1 = styled.h1`
  margin-bottom: 20px;
  font-size: 2.8rem;
  @media ${u.device.desktop} {
    font-size: 3.2rem;
  }
`;

S.MyCourses = styled.div`
  margin-bottom: 30px;
`;

S.PickUp = styled.h2`
  margin-bottom: 30px;
`;

S.ShowMoreButton = styled(Button).attrs(() => ({
  text: "Show more",
  isSecondary: true,
}))`
  margin: 40px auto 0;
  display: ${(props) => (props.hide ? "none" : "flex")};
`;

S.Hr = styled.hr`
  border: 1px solid ${u.colours.shellOverlay};
  margin: 30px auto;
  width: 80%;
  max-width: 1440px;
`;

S.Lessons = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 15px;
  column-gap: 1%;
  @media ${u.device.tablet} {
    row-gap: 25px;
    justify-content: space-between;
  }
`;

S.Button = styled(Button)`
  margin: auto;
`;

S.CategoriesHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

S.Filters = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  align-items: center;
  @media (min-width: 750px) {
    flex-direction: row;
    > * {
      padding-left: 20px;
    }
  }
`;

S.SearchAndFiltersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${Device.tablet}) {
    margin-top: 40px;
    margin-bottom: 10px;
  }
`;

S.Left = styled.div`
  flex: 1;
`;
